import React from 'react';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { CareerCard } from '../types/careerCard';

// Define the ToastType to match the app's type
type ToastType = 'success' | 'error' | 'info';

/**
 * Handles the completion of to-do items in Project cards, moving them to the completed tasks section
 * 
 * @param toDo The to-do item to complete
 * @param index The index of the to-do in the active_to_do array
 * @param editedCard The current card data
 * @param onCardChange Function to update the card data
 * @param setActionInProgress Function to manage loading state
 * @param showToast Function to display toast notifications
 * @returns Promise<void>
 */
export const handleCompleteToDo = async (
  toDo: any,
  index: number,
  editedCard: CareerCard,
  onCardChange: (updatedCard: CareerCard) => void,
  setActionInProgress: (action: string | null) => void,
  showToast: (message: React.ReactNode, type: ToastType, onUndo?: () => void) => void
): Promise<void> => {
  try {
    setActionInProgress(`toDo_${index}`);

    // Create a timestamp for when this to-do was completed
    const completionDate = new Date().toISOString().split('T')[0];

    // Create a new entry for completed tasks
    const taskEntry = {
      id: toDo.id || `task_${Date.now().toString(36)}`,
      title: toDo.title || 'Completed To Do Item',
      description: toDo.description || '',
      actions_taken: toDo.notes || '',
      completion_date: completionDate,
      outcome: toDo.outcome || `Completed "${toDo.title || 'to-do item'}"`,
      career_impact: toDo.career_impact || "3. Moderate Impact",
      engagement_level: toDo.engagement_level || "3. Neutral"
    };

    // Update card data
    const updatedCard = { ...editedCard };

    // Add the entry to completed_tasks if it exists, otherwise create it
    const completedTasks = Array.isArray(updatedCard.card_data.completed_tasks) 
      ? [...updatedCard.card_data.completed_tasks] 
      : [];
      
    completedTasks.push(taskEntry);
    updatedCard.card_data.completed_tasks = completedTasks;

    // Remove the to-do from active_to_do
    const activeToDos = Array.isArray(updatedCard.card_data.active_to_do) 
      ? [...updatedCard.card_data.active_to_do] 
      : [];
      
    activeToDos.splice(index, 1);
    updatedCard.card_data.active_to_do = activeToDos;

    // Update the card
    onCardChange(updatedCard);
    
    showToast(
      <div className="flex items-center justify-between w-full gap-4">
        <span>To Do item moved to Completed Tasks</span>
      </div>,
      'success'
    );
  } catch (error) {
    console.error("Error completing to-do:", error);
    showToast('Failed to complete to-do item', 'error');
  } finally {
    setActionInProgress(null);
  }
};

/**
 * Renders the action buttons for to-do items in Project cards
 * 
 * @param toDo The to-do item
 * @param index The index of the to-do in the active_to_do array
 * @param editedCard The current card data
 * @param onCardChange Function to update the card data
 * @param actionInProgress Current loading state
 * @param setActionInProgress Function to manage loading state
 * @param showToast Function to display toast notifications
 * @param isEditMode Whether the card is in edit mode
 * @returns React.ReactNode | null
 */
export const renderToDoActions = (
  toDo: any,
  index: number,
  editedCard: CareerCard,
  onCardChange: (updatedCard: CareerCard) => void,
  actionInProgress: string | null,
  setActionInProgress: (action: string | null) => void,
  showToast: (message: React.ReactNode, type: ToastType, onUndo?: () => void) => void,
  isEditMode: boolean
): React.ReactNode | null => {
  if (!isEditMode) return null;
  
  return (
    <div className="follow-up-actions flex space-x-2 mt-2 justify-end">
      <button
        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center text-sm"
        onClick={() => handleCompleteToDo(
          toDo, 
          index, 
          editedCard, 
          onCardChange, 
          setActionInProgress, 
          showToast
        )}
        disabled={actionInProgress === `toDo_${index}`}
        title="Mark as complete"
      >
        <IoCheckmarkCircleOutline className="mr-1" /> 
        Complete
      </button>
    </div>
  );
}; 